import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, FileText } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StageBadge } from "@/components/Badges";
import { api, formatErr, inr, STAGE, fmtDate } from "@/lib/api";

const EQUIP = ["ICU Beds", "Patient Monitors", "MGPS Pipeline", "Radiology", "Ventilators", "Other"];
const EMPTY = { hospital_name: "", contact_person: "", phone_number: "", equipment: [], estimated_deal_value: "", follow_up_date: "", city: "Varanasi", notes: "" };

const LeadRow = ({ l, onStage }) => (
  <div className="bg-white rounded-xl border p-4" data-testid={`my-lead-${l.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="font-semibold text-slate-900">{l.hospital_name}</div>
        <div className="text-xs text-slate-500">{l.equipment_inquired} · {l.city}</div>
      </div>
      <div className="font-mono-num font-semibold text-slate-900">{inr(l.estimated_deal_value)}</div>
    </div>
    <div className="flex items-center justify-between mt-3 gap-2">
      <div className="text-xs text-slate-500">Follow-up {fmtDate(l.follow_up_date)}</div>
      <Link to={`/app/quotes/new?lead=${l.id}`} className="ml-auto text-xs font-semibold text-sky-700 inline-flex items-center gap-1" data-testid={`lead-make-quote-${l.id}`}><FileText className="h-3.5 w-3.5" />Quote</Link>
      <Select value={l.stage} onValueChange={(v) => onStage(l.id, v)}>
        <SelectTrigger className="w-40 h-8 text-xs" data-testid={`lead-stage-select-${l.id}`}><StageBadge stage={l.stage} /></SelectTrigger>
        <SelectContent className="bg-white">{Object.entries(STAGE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  </div>
);

export default function AddLead() {
  const [f, setF] = useState(EMPTY);
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => {
    load();
  }, []);

  const toggleEq = (e) => setF((p) => ({ ...p, equipment: p.equipment.includes(e) ? p.equipment.filter((x) => x !== e) : [...p.equipment, e] }));

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", {
        hospital_name: f.hospital_name, contact_person: f.contact_person, phone_number: f.phone_number,
        equipment_inquired: f.equipment.join(", "), estimated_deal_value: Number(f.estimated_deal_value || 0),
        follow_up_date: f.follow_up_date || null, city: f.city, notes: f.notes, stage: "new",
      });
      toast.success("Lead saved as New Inquiry");
      setF(EMPTY);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const onStage = async (id, stage) => {
    try {
      await api.put(`/leads/${id}`, { stage });
      toast.success(`Updated to ${STAGE[stage]}`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-extrabold text-slate-900 tracking-tight">Add Lead</h1>
      <form onSubmit={submit} className="bg-white rounded-2xl border p-5 space-y-4" data-testid="add-lead-form">
        <div><Label>Hospital / Clinic Name *</Label><Input required value={f.hospital_name} onChange={set("hospital_name")} data-testid="lead-hospital-input" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Doctor / Procurement Contact</Label><Input value={f.contact_person} onChange={set("contact_person")} data-testid="lead-contact-input" /></div>
          <div><Label>Phone</Label><Input value={f.phone_number} onChange={set("phone_number")} inputMode="tel" data-testid="lead-phone-input" /></div>
        </div>
        <div>
          <Label>Equipment Required</Label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {EQUIP.map((e) => (
              <button type="button" key={e} onClick={() => toggleEq(e)} data-testid={`lead-equipment-${e.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${f.equipment.includes(e) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-700 hover:border-sky-400"}`}>{e}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Estimated Value (₹)</Label><Input type="number" min="0" value={f.estimated_deal_value} onChange={set("estimated_deal_value")} className="font-mono-num" data-testid="lead-value-input" /></div>
          <div><Label>Next Follow-Up</Label><Input type="date" value={f.follow_up_date} onChange={set("follow_up_date")} data-testid="lead-followup-input" /></div>
        </div>
        <div><Label>City / District</Label><Input value={f.city} onChange={set("city")} data-testid="lead-city-input" /></div>
        <div><Label>Notes</Label><Textarea value={f.notes} onChange={set("notes")} data-testid="lead-notes-input" /></div>
        <Button type="submit" disabled={busy || f.hospital_name.length < 2} className="w-full h-11 bg-sky-600 hover:bg-sky-700" data-testid="add-lead-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-2" />Save Lead</>}
        </Button>
      </form>
      <section>
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-3">My Leads ({leads.length})</h2>
        <div className="space-y-3" data-testid="my-leads-list">
          {leads.map((l) => <LeadRow key={l.id} l={l} onStage={onStage} />)}
          {leads.length === 0 && <div className="text-sm text-slate-500 text-center p-6 border border-dashed rounded-xl bg-white">No leads yet.</div>}
        </div>
      </section>
    </div>
  );
}
